import { useCallback, useEffect, useRef, useState } from 'react'

import {
  fetchGeneratedImages,
  fetchImageGenerationCredits,
  sendGenerateImagesRequest,
} from 'api/generate-images'
import useInterval from 'hooks/useInterval'

import buildGenerateImagesRequestBody from './generateImagesTransforms'

const POLL_INTERVAL_MS = 30000

export function resolveCoCreateVariantFromStatus(data) {
  if (!data) return 'closed'

  const { status, ready } = data

  if (status === 'STARTED') return 'generating'
  if (status === 'SUCCESS' && ready) return 'ready'
  if (status === 'SUCCESS' && !ready) return 'generating'
  if (status === 'FAILURE') return 'closed'

  return 'closed'
}

export default function useCoCreateImagePanel({ eventId, tc, tcRunId, onClearGenerating }) {
  const [variant, setVariantState] = useState('closed')
  const [generatedResult, setGeneratedResult] = useState(null)
  const [generationFailed, setGenerationFailed] = useState(false)
  const [fetchStatusFailed, setFetchStatusFailed] = useState(false)
  const [creditStatus, setCreditStatus] = useState(null)
  const [creditStatusError, setCreditStatusError] = useState(null)
  const [creditStatusLoading, setCreditStatusLoading] = useState(true)
  const creditRequestIdRef = useRef(0)

  const setVariant = useCallback((next) => {
    setVariantState((prev) => (typeof next === 'function' ? next(prev) : next))
  }, [])

  const refreshCreditStatus = useCallback(async () => {
    const requestId = creditRequestIdRef.current + 1
    creditRequestIdRef.current = requestId
    setCreditStatusLoading(true)
    const response = await fetchImageGenerationCredits()

    if (creditRequestIdRef.current !== requestId) {
      return response
    }

    if (response.success) {
      setCreditStatus(response.data)
      setCreditStatusError(null)
    } else {
      setCreditStatus(null)
      setCreditStatusError(response.error || 'entitlements_unavailable')
    }

    setCreditStatusLoading(false)
    return response
  }, [])

  useEffect(() => {
    refreshCreditStatus()
    return () => {
      creditRequestIdRef.current += 1
    }
  }, [eventId, tc, tcRunId, refreshCreditStatus])

  useEffect(() => {
    if (variant === 'open') {
      setGenerationFailed(false)
    }
  }, [variant])

  const applyStatusResponse = useCallback((success, data, status) => {
    if (!success) {
      setGeneratedResult(null)

      if (status === 404) {
        setVariant('closed')
      } else {
        setFetchStatusFailed(true)
      }

      return
    }

    setFetchStatusFailed(false)

    if (data?.status === 'FAILURE') {
      const isCreditFailure = [
        'entitlements_unavailable',
        'insufficient_credits',
        'v4_acceptance_required',
      ].includes(data?.error)

      setGenerationFailed(!isCreditFailure)
      setGeneratedResult(null)
      setVariant(isCreditFailure ? 'open' : 'closed')
      onClearGenerating?.()
      refreshCreditStatus()
      return
    }

    const statusVariant = resolveCoCreateVariantFromStatus(data)

    if (statusVariant === 'ready') {
      setGeneratedResult(data.result ?? null)
      refreshCreditStatus()
    } else {
      setGeneratedResult(null)
    }

    setVariant((prev) => {
      if (prev === 'open' && statusVariant === 'closed') return prev
      return statusVariant
    })
  }, [setVariant, onClearGenerating, refreshCreditStatus])

  const checkStatus = useCallback(async () => {
    if (!eventId || !tc || tcRunId == null) return

    const response = await fetchGeneratedImages(eventId, tc, tcRunId)
    applyStatusResponse(response.success, response.data, response.status)
  }, [eventId, tc, tcRunId, applyStatusResponse])

  // Fetch only when campaign identity changes — not when parent callbacks recreate
  useEffect(() => {
    checkStatus()
  }, [eventId, tc, tcRunId]) // eslint-disable-line react-hooks/exhaustive-deps -- intentional: avoid refetch on checkStatus identity

  useInterval(
    checkStatus,
    variant === 'generating' ? POLL_INTERVAL_MS : null,
    () => variant === 'generating',
  )

  const generateImages = useCallback(async ({
    media,
    selectedSubGroupId,
    ratioVariants,
    headlines = [],
    requiredSlotRatios,
  }) => {
    if (!eventId || !tc || tcRunId == null) {
      return { success: false }
    }

    const body = buildGenerateImagesRequestBody({
      eventId,
      tc,
      tcRunId,
      media,
      selectedSubGroupId,
      ratioVariants,
      headlines,
      requiredSlotRatios,
    })

    if (!body.media.length) {
      return { success: false }
    }

    setGenerationFailed(false)
    setVariant('generating')

    const response = await sendGenerateImagesRequest(body)

    if (!response.success) {
      if (response.data?.credit_access || response.data?.remaining != null) {
        setCreditStatus((current) => ({
          ...(current ?? {}),
          ...(response.data.credit_access && { credit_access: response.data.credit_access }),
          ...(response.data.terms && { terms: response.data.terms }),
          ...(response.data.billing_portal_url && {
            billing_portal_url: response.data.billing_portal_url,
          }),
          credits: {
            ...(current?.credits ?? {}),
            ...(response.data.remaining != null && { remaining: response.data.remaining }),
          },
        }))
        setCreditStatusError(null)
      } else if (response.data?.error === 'entitlements_unavailable') {
        setCreditStatusError(response.error || response.data.error)
      }
      setVariant('open')
      return response
    }

    if (response.data?.credits?.remaining != null) {
      setCreditStatus((current) => ({
        ...(current ?? {}),
        credits: {
          ...(current?.credits ?? {}),
          remaining: response.data.credits.remaining,
        },
      }))
    }

    await checkStatus()
    return {
      ...response,
      groupId: body.group_id ?? null,
    }
  }, [eventId, tc, tcRunId, checkStatus, setVariant])

  return {
    variant,
    setVariant,
    generatedResult,
    generationFailed,
    fetchStatusFailed,
    checkStatus,
    generateImages,
    creditStatus,
    creditStatusError,
    creditStatusLoading,
    refreshCreditStatus,
  }
}
